// ABOUTME: Super-admin modal that fetches an order or import order live from its webshop and shows the raw JSON.
// ABOUTME: Calls GET /orders/:id/webshopjson or /importorders/:id/webshopjson when opened, with a copy button.

import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import Shbutton from "../../theme/buttons/Shbutton";
import ShCopyButton from "../../theme/ShCopy";
import { useGetOrderWebshopJsonQuery } from "../../../newapi/order/ordersSlice";
import { useGetImportOrderWebshopJsonQuery } from "../../../newapi/order/importOrderSlice";

const MOrderWebshopJson = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    // An import order (failed or ignored import) has no order yet, so it is fetched by its own id
    const importOrderId = props.importOrder?.id;
    const orderId = importOrderId ? null : props.orderData?.id;

    const orderQuery = useGetOrderWebshopJsonQuery(orderId, {
        skip: !props.open || !orderId,
    });
    const importOrderQuery = useGetImportOrderWebshopJsonQuery(importOrderId, {
        skip: !props.open || !importOrderId,
    });
    const { data, error, isFetching } = importOrderId ? importOrderQuery : orderQuery;

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const payloadText = data?.data ? JSON.stringify(data.data, null, 2) : null;

    return (
        <SHModal
            open={props.open}
            onClose={props.handleClose}
        >
            <Box sx={{ width: 1, maxWidth: 800 }}>
                <Box
                    sx={{
                        textAlign: "center",
                        paddingY: 4,
                        paddingX: 5,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                    }}
                >
                    <Subheading>{t("webshop_json")}</Subheading>
                    <Text center light>{t("webshop_json_subtitle")}</Text>
                </Box>

                <Box
                    sx={{
                        background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        padding: 3,
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Box>
                        <Text light sx={{mr: 1}}>{t("order")}</Text>
                        <Text bold>{importOrderId ? props.importOrder?.webshopreference : (props.orderData?.barcode ?? orderId)}</Text>
                        {data?.webshopType &&
                            <Text light sx={{ml: 1}}>{data.webshopType} {data.externalId}</Text>
                        }
                    </Box>

                    {isFetching &&
                        <Text light>{t("loading")}</Text>
                    }

                    {!isFetching && error &&
                        <Text>{error?.data?.error ?? t("webshop_json_failed")}</Text>
                    }

                    {!isFetching && payloadText &&
                        <Box
                            sx={{
                                position: "relative",
                                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                                borderRadius: 3,
                                p: 2,
                                pr: 7,
                                maxHeight: "60vh",
                                overflow: "auto",
                            }}
                        >
                            <Box
                                component="pre"
                                sx={{
                                    m: 0,
                                    fontSize: 12,
                                    fontFamily: "monospace",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-all",
                                    color: colors.txt["primary"],
                                }}
                            >
                                {payloadText}
                            </Box>
                            <ShCopyButton
                                sx={{ position: "absolute", top: 8, right: 8 }}
                                closeAfter={1500}
                                value={payloadText}
                            />
                        </Box>
                    }
                </Box>

                <Box
                    component="footer"
                    sx={{
                        width: 1,
                        paddingBottom: isStandAlone ? "32px" : "16px",
                        display: "flex",
                        alignItems: "center",
                        paddingRight: 3,
                        zIndex: 1058,
                        position: "relative",
                        paddingTop: 2,
                        background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        borderBottomLeftRadius: 24,
                        borderBottomRightRadius: 24,
                        justifyContent: "space-between",
                        "@media screen and (max-width: 47.9375em)": {
                            paddingRight: 0,
                        }
                    }}
                >
                    <Shbutton isCancel={true} className={"always-visible"} onClick={props.handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon />}>{t("close")}</Shbutton>
                </Box>
            </Box>
        </SHModal>
    );
};

export default MOrderWebshopJson;
